import React, { useEffect } from "react";
import ToolbarHeader from "../../components/ToolbarHeader";
import CustomButton from "../../components/CustomButton";
import CustomTable from "../../components/CustomTable";
import moment from "moment";
import DatePicker from "../../components/FormFields/DatePicker";
import RadioButton from "../../components/FormFields/RadioButtons";
import { useState } from "react";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import { icons } from "../../assets/icons";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import RejectModal from "../../CustomRejectModal/RejectModal";
import EditApproveModal from "../../CustomEditApproveModal/EditApproveModal";
import {displayDateFormat, getArrayOfSelectedStoresCode} from "../../utils/helpers"
import { buildStandaloneDmsApprovePayload } from "../../utils/dmsStandaloneListUtils";

const sanitizeActionComment = (comment) => {
  if (!comment) return "";
  const trimmed = String(comment).trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower === "bulk send all - approved") return "Approved";
  if (lower === "bulk send all - rejected") return "Rejected";
  if (lower === "resubmitted after rework") return "";
  return comment;
};

const PettyCash = () => {
  const { data = [], apiCall } = useApi();
  const { data: data1, apiCall: apiCall1 } = useApi();
  const {
    user = {},
    selectedStores,
    storeDropdownState,
    setCurrentRadioSelect,
    currentRadioSelect = "ALL_REQUEST",
  } = useAuth();

  const { storeCode = "" } = selectedStores?.storeDetails || selectedStores?.[0] || {}

  const isStoreActive = user.userLabel == "Store";
  // const {
  //   user: { userLabel = "" },
  //   selectedStores,
  // } = useAuth();
  const [selectedRadio, setSelectedRadio] = useState(currentRadioSelect);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [startDate, setStartDate] = useState(
    moment(
      searchParams.get("startDate") || moment().subtract({ days: 7 }).toDate()
    ).toDate()
  );
  const [endDate, setEndDate] = useState(
    moment(searchParams.get("endDate") || new Date()).toDate()
  );
  const [isRejectVisible, setIsRejectVisible] = useState({
    isOpen: false,
    data: [],
  });
  const [isEditApproveVisible, setIsEditApproveVisible] = useState({
    isOpen: false,
    data: [],
  });

  const normalizePettyCashListResponse = (rawResponse) => {
    const response = rawResponse || {};

    const docs = Array.isArray(response?.documents) ? response.documents : [];
    const storeWise = Array.isArray(response?.storeWiseRecordsResponses)
      ? response.storeWiseRecordsResponses
      : [];

    const normalizeApprovalStatusCode = (rawStatus) => {
      if (!rawStatus) return null;
      const s = String(rawStatus).trim();
      if (!s) return null;

      // If backend already returned the technical code, keep it as-is.
      const known = [
        "L1ApprovalPending",
        "L1Rejected",
        "L2ApprovalPending",
        "L2Rejected",
        "AccountsApprovalPending",
        "AccountsRejected",
        "Approved",
      ];
      if (known.includes(s)) return s;

      if (/^Approved$/i.test(s)) return "Approved";
      if (/^L1\s+Pending$/i.test(s)) return "L1ApprovalPending";
      if (/^L1\s+Rejected$/i.test(s)) return "L1Rejected";
      if (/^L2\s+Pending$/i.test(s)) return "L2ApprovalPending";
      if (/^L2\s+Rejected$/i.test(s)) return "L2Rejected";
      if (/^(Accounts|Finance)\s+Pending$/i.test(s) || /^L3\s+Pending$/i.test(s)) return "AccountsApprovalPending";
      if (/^(Accounts|Finance)\s+Rejected$/i.test(s) || /^L3\s+Rejected$/i.test(s)) return "AccountsRejected";

      // Handle variants like "L1 Approval Pending" (with spaces).
      if (/^L1\s*Approval\s*Pending$/i.test(s)) return "L1ApprovalPending";
      if (/^L2\s*Approval\s*Pending$/i.test(s)) return "L2ApprovalPending";
      if (/^Accounts\s*Approval\s*Pending$/i.test(s)) return "AccountsApprovalPending";

      return null;
    };

    /** getAllRecords often omits storeCode for imprest; description may contain e.g. "Store ID-S265" or "S264". */
    const parseStoreCodeFromDescription = (text) => {
      if (text == null || String(text).trim() === "") return "";
      const s = String(text);
      const m1 = s.match(/Store\s*ID[-:\s]*([A-Za-z0-9]+)/i);
      if (m1) return m1[1].trim();
      const m2 = s.match(/\b(S\d{2,6})\b/);
      if (m2) return m2[1].trim();
      return "";
    };

    const resolveStoreIdentity = (doc, storeRow = null) => {
      const docStore = doc?.store || {};
      const rowStore = storeRow?.store || {};
      const attrs = Array.isArray(doc?.dataModalAttributeSet)
        ? doc.dataModalAttributeSet
        : [];
      const attrVal = (names) => {
        for (const n of names) {
          const a = attrs.find((x) => x?.metadataTechnicalName === n);
          const v = a?.attributeValue;
          if (v != null && String(v).trim() !== "") {
            return String(v).trim();
          }
        }
        return "";
      };
      const fromDescField =
        parseStoreCodeFromDescription(doc?.description) ||
        parseStoreCodeFromDescription(attrVal(["description"]));
      const storeCode =
        doc?.storeCode ||
        storeRow?.storeCode ||
        docStore?.storeCode ||
        docStore?.code ||
        rowStore?.storeCode ||
        rowStore?.code ||
        attrVal(["store_code", "storeCode", "STORE_CODE", "store"]) ||
        fromDescField ||
        "";
      const storeName =
        doc?.storeName ||
        storeRow?.storeName ||
        docStore?.name ||
        docStore?.storeName ||
        rowStore?.name ||
        rowStore?.storeName ||
        attrVal(["store_name", "storeName", "STORE_NAME"]) ||
        "";
      return { storeCode, storeName };
    };

    const normalizeFromDocuments = (doc) => {
      const attributes = Array.isArray(doc?.dataModalAttributeSet)
        ? doc.dataModalAttributeSet
        : [];
      const byTechnicalName = {};
      attributes.forEach((a) => {
        const key = a?.metadataTechnicalName;
        if (key) byTechnicalName[key] = a;
      });

      const getAttr = (technicalName) =>
        byTechnicalName?.[technicalName]?.attributeValue;

      const workflowSteps = Array.isArray(doc?.workflowStepResponses)
        ? doc.workflowStepResponses
        : [];
      const findStep = (label) =>
        workflowSteps.find((s) => s?.userLabel === label) || null;

      const l1 = findStep("L1");
      const l2 = findStep("L2");
      const financeStep = findStep("Finance");
      const { storeCode, storeName } = resolveStoreIdentity(doc);

      return {
        parentId: doc?.id,
        id: doc?.id,
        storeCode,
        storeName,
        date: getAttr("date") || null,
        description: getAttr("description") || "",
        amount: getAttr("amount") ?? 0,

        l1_amount_approved: getAttr("l1_amount_approved") ?? 0,
        l2_amount_approved: getAttr("l2_amount_approved") ?? 0,
        finance_amount_approved: getAttr("finance_amount_approved") ?? 0,

        L1: l1 ? { ...l1, status: l1?.status || l1?.action } : { status: "NA" },
        L2: l2 ? { ...l2, status: l2?.status || l2?.action } : { status: "NA" },
        Finance: financeStep
          ? { ...financeStep, status: financeStep?.status || financeStep?.action }
          : { status: "NA" },

        // Keep backend-provided label if present
        status: doc?.status || "",
        // Used by the details header labelSplitFun().
        approval_status: normalizeApprovalStatusCode(getAttr("approval_status") || doc?.status) || null,
      };
    };

    if (docs.length > 0) {
      return docs.map(normalizeFromDocuments).filter((x) => x?.parentId != null);
    }

    // Store branch: backend can return rows in documents or dataModalRecordsResponse
    return storeWise
      .flatMap((s) => {
        const childRows = Array.isArray(s?.documents)
          ? s.documents
          : Array.isArray(s?.dataModalRecordsResponse)
          ? s.dataModalRecordsResponse
          : [];
        return childRows.map((doc) => ({ doc, storeRow: s }));
      })
      .map(({ doc, storeRow }) => {
        const l1 = doc?.l1 || null;
        const l2 = doc?.l2 || null;
        const l3 = doc?.l3 || null; // sometimes populated depending on naming
        const finance = l3 || doc?.finance || null;
        const { storeCode, storeName } = resolveStoreIdentity(doc, storeRow);

        return {
          parentId: doc?.id,
          id: doc?.id,
          storeCode,
          storeName,
          date: doc?.submittedOn || null,
          description: doc?.description || "",
          amount: doc?.amount ?? 0,

          approved_amount: doc?.approvedAmount ?? 0,
          l1_amount_approved: l1?.approvedAmount ?? 0,
          l2_amount_approved: l2?.approvedAmount ?? 0,
          finance_amount_approved: finance?.approvedAmount ?? 0,

          L1: l1 ? { ...l1, status: l1?.status || l1?.action } : { status: "NA" },
          L2: l2 ? { ...l2, status: l2?.status || l2?.action } : { status: "NA" },
          Finance: finance
            ? { ...finance, userLabel: "Finance", status: finance?.status || finance?.action }
            : { status: "NA" },

          status: doc?.status || "",
          // Used by the details header labelSplitFun().
          approval_status:
            normalizeApprovalStatusCode(doc?.status) ||
            normalizeApprovalStatusCode(l1?.status ? `L1 ${l1.status}` : null) ||
            normalizeApprovalStatusCode(l2?.status ? `L2 ${l2.status}` : null) ||
            normalizeApprovalStatusCode(finance?.status ? `Accounts ${finance.status}` : null) ||
            null,
        };
      })
      .filter((x) => x?.parentId != null);
  };
  const onChangeRadio = (e, status) => {
    setSelectedRadio(e.target.value);
    setCurrentRadioSelect(e.target.value);
    // pettyCashTableDataApi(startDate, endDate, status);
  };

  const onClickRequest = () => {
    navigate("/requestimprest");
  };

  useEffect(() => {

    // if(isStoreActive){
      pettyCashTableDataApi(startDate, endDate, selectedRadio);
    // } else {
    //   if(storeCode.length > 0){
    //     pettyCashTableDataApi(startDate, endDate, selectedRadio);
    //   }
    // }
      // pettyCashTableDataApi(startDate, endDate, selectedRadio);

  }, [selectedStores, selectedRadio, user]);

  const pettyCashTableDataApi = (start, end, status) => {
    const nextSearch = createSearchParams({
      startDate: start,
      endDate: end,
    }).toString();
    const currentSearch = window.location.search?.startsWith("?")
      ? window.location.search.slice(1)
      : window.location.search || "";
    if (currentSearch !== nextSearch) {
      navigate(
        {
          pathname: window.location.pathname,
          search: nextSearch,
        },
        { replace: true }
      );
    }

      apiCall({
        url: apiUrls.dmsService.getRecords,
        dataToSend: {
          startDate: moment(start).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
          metadataSchemaId: 6, // for Impreset Request metadataSchemaId is 6
          endDate: moment(end || start)
            .endOf("d")
            .format("YYYY-MM-DD HH:mm:ss"),
          searchText: "",
          status: status === "ALL_REQUEST" ? null : status,
          storeCodes: getArrayOfSelectedStoresCode(selectedStores, storeDropdownState)
          // storeCodes: selectedStores?.storeDetails?.storeCode
          //   ? selectedStores?.storeDetails?.storeCode !== "All" ? [selectedStores?.storeDetails?.storeCode] : null
          //   : user.userLabel == "Store" ? null : storeCode ? [storeCode] : [selectedStores[0]?.storeCode],
        },
        method: "post",
        processData: (raw) => normalizePettyCashListResponse(raw),
      });
  };

  const ImprestRequestApi = (payload, status) => {
    apiCall1({
      url: apiUrls.dmsService.approveRequest,
      dataToSend: payload,
      method: "post",
      onSuccess: () => {
        pettyCashTableDataApi(startDate, endDate, selectedRadio);
        toast.success(status);
        setIsRejectVisible({ ...isRejectVisible, isOpen: false });
        setIsEditApproveVisible({ ...isEditApproveVisible, isOpen: false });
      },
    });
  };

  const onChangeDate = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end !== null) pettyCashTableDataApi(start, end, selectedRadio);
  };

  const approveRequestHandler = (id, comment = "") => {
    ImprestRequestApi(
      buildStandaloneDmsApprovePayload({
        approved: id,
        comment,
        rejected: [],
        saveDecisionOnly: false,
      }),
      "Approved Request Successfully"
    );
  };

  const rejectRequestHandler = (id, comment = "") => {
    ImprestRequestApi(
      buildStandaloneDmsApprovePayload({
        approved: [],
        comment,
        rejected: id,
        saveDecisionOnly: false,
      }),
      "Rejected Request Successfully"
    );
  };

  const approveRejectButtonStage = (key) => {
    const allState = key.row.original || {};
    let status = allState[user?.userLabel || ""]?.status || "";
    switch (status) {
      case "Rejected":
        return false;

      case "Approved":
        return false;

      case "Pending":
        return true;

      default:
        return false;
    }
  };

  const canShowDescription =
    user?.userLabel !== "Store" &&
    Array.isArray(data) &&
    data.some(
      (r) => r?.description != null && String(r?.description).trim().length > 0
    );

  const conditionalColumns =
    user?.userLabel !== "Store"
      ? [
          ...(canShowDescription
            ? [
                {
                  Header: "Description",
                  accessor: "description",
                  Cell: (props) => {
                    return (
                      <div className="">
                        {props?.row?.original?.description}
                      </div>
                    );
                  },
                },
              ]
            : []),
          { 
            Header: "Request Amount (₹)", 
            accessor: "amount",
            Cell: (props) => {
              const value = props?.row?.original?.amount;
              let amount;
              if(typeof value == "string"){
                amount = Number(value).toFixed(2);
              } else {
                amount = value
              }
              return (
                <div>{amount}</div>
              );
            }, 
          },
        ]
      : [
          { 
            Header: "Amount (₹)", 
            accessor: "amount",
            Cell: (props) => {
              const value = props?.row?.original?.amount;
              let amount;
              if(typeof value == "string"){
                amount = Number(value).toFixed(2);
              } else {
                amount = value
              }
              return (
                <div>{amount}</div>
              );
            },
          },
          {
            Header: "L1 Status",
            accessor: "l1_status",
            Cell: (props) => {
              const value = props?.row?.original?.L1?.status;
              if (!value) {
                return "-";
              }
              return (
                <div
                  className={
                    value === "Approved"
                      ? "text-[var(--successGreen)]"
                      : value === "Rejected"
                      ? "text-[var(--textRed)]"
                      : value === "Pending" ? "text-[var(--pendingYellow)]" : "text-[var(--pendingYellow)]"
                  }
                >
                  {value}
                </div>
              );
            },
          },
          {
            Header: "L2 Status",
            accessor: "l2_status",
            Cell: (props) => {
              const value = props?.row?.original?.L2?.status;
              if (!value) {
                return "-";
              }
              return (
                <div
                  className={
                    value === "Approved"
                      ? "text-[var(--successGreen)]"
                      : value === "Rejected"
                      ? "text-[var(--textRed)]"
                      : value === "Pending" ? "text-[var(--pendingYellow)]" : "text-[var(--pendingYellow)]"
                  }
                >
                  {value}
                </div>
              );
            },
          },
          {
            Header: "A/C Status",
            accessor: "ac_status",
            Cell: (props) => {
              const value = props?.row?.original?.Finance?.status;
              if (!value) {
                return "-";
              }
              return (
                <div
                  className={
                    value === "Approved"
                      ? "text-[var(--successGreen)]"
                      : value === "Rejected"
                      ? "text-[var(--textRed)]"
                      : value === "Pending" ? "text-[var(--pendingYellow)]" : "text-[var(--pendingYellow)]"
                  }
                >
                  {value}
                </div>
              );
            },
          },
        ];

  const extraConditionalColumns =
    user?.userLabel === "L2"
      ? [
          { Header: "L1 Approved Amount (₹)", accessor: "l1_amount_approved" },
          { Header: "L1 Comment", accessor: (row) => sanitizeActionComment(row?.L1?.actionComment) },
        ]
      : user?.userLabel === "Finance"
      ? [
          { Header: "L2 Approved Amount (₹)", accessor: "l2_amount_approved" },
          { Header: "L2 Comment", accessor: (row) => sanitizeActionComment(row?.L2?.actionComment) },
        ]
      : [];

  const columns = [
    {
      Header: "ID",
      accessor: "id",
      Cell: (props) => {
        const parentId = props?.row?.original?.parentId;
        const allData = props?.row?.original;
        return (
          <div
            className="cursor-pointer underline underline-offset-4"
            style={{ color: "blue" }}
            onClick={() => {
              navigate(`/petty-cash/${parentId}`, { state: allData });
            }}
          >
            {parentId}
          </div>
        );
      },
    },
    {
      Header: "Store Code",
      accessor: "storeCode",
      Cell: (props) => {
        const row = props?.row?.original || {};
        // Store Code column must not fall back to storeName (would show the wrong label).
        return <div>{row?.storeCode ? String(row.storeCode) : "-"}</div>;
      },
    },
    {
      Header: "Submit On",
      accessor: "date",
      Cell: (props) => {
        return <div>{moment(props?.row?.original?.date).format(displayDateFormat)}</div>;
      },
    },

    ...conditionalColumns,
    ...extraConditionalColumns,
    {
      Header: "Approved Amount (₹)",
      accessor: "approved_amount",
      Cell: (props) => {
        let appvAmount;
        let rowDa = props?.row?.original || {}
        if(user?.userLabel === "L1") {
          appvAmount = rowDa?.l1_amount_approved || ""
        } else if(user?.userLabel === "L2"){
          appvAmount = rowDa?.l2_amount_approved || ""
        } else if(user?.userLabel === "Finance") {
          appvAmount = rowDa?.finance_amount_approved || ""
        } else {
          appvAmount = rowDa?.finance_amount_approved || rowDa?.l2_amount_approved || 
          rowDa?.l1_amount_approved || ""
          //   props?.row?.original?.Finance?.status === "Approved";
          // const value = props?.row?.original?.amount;
        }
        return appvAmount ? Number(appvAmount).toFixed(2) : "-"

      },
    },
    {
      Header: "",
      show: false,
      accessor: "action",
      Cell: (props) => {
        const id = props?.row?.original?.parentId;
        const editedAmount = props?.row?.original?.finance_amount_approved ||
        props?.row?.original?.l2_amount_approved ||
          props?.row?.original?.l1_amount_approved ||
          props?.row?.original?.amount || 0;
        return (
          <>
            {approveRejectButtonStage(props) && (
              <div className="flex">
                <img
                  alt="edit icon"
                  src={icons.editIcon}
                  className="w-[2.4rem] h-[2.4rem] mr-4 pl-2 cursor-pointer"
                  onClick={() =>
                    setIsEditApproveVisible({
                      data: [{ id: id, approvedAmount: editedAmount, previousAm: editedAmount, totalAmount: editedAmount }],
                      isOpen: true,
                      previousAmounts: [{ id: id, approvedAmount: editedAmount, previousAm: editedAmount, totalAmount: editedAmount }]
                    })
                  }
                />
                <img
                  alt="approve icon"
                  src={icons.squareTickIcon}
                  className="w-[2.4rem] h-[2.4rem] mr-4 pl-2 cursor-pointer"
                  onClick={() => approveRequestHandler([{id: id, approvedAmount: editedAmount}])}
                />
                <img
                  alt="reject icon"
                  src={icons.squareTimesIcon}
                  className="w-[2.4rem] h-[2.4rem] pl-2 cursor-pointer"
                  onClick={() =>
                    setIsRejectVisible({ data: [id], isOpen: true })
                  }
                />
              </div>
            )}
          </>
        );
      },
    },
  ];
  // const { storeCode = ""} = selectedStores?.storeDetails || {}
  // if(storeCode.length > 0){
  //   pettyCashTableDataApi(startDate, endDate, selectedRadio);
  // }
  return (
    <div className="dashboard-container">
      <ToolbarHeader
        title="Petty Cash"
        showBack={false}
        containerClassName="mt-14"
        renderRight={() => {
          if (user?.userLabel == "Store") {
            return (
              <CustomButton
                title="Request Imprest Amount"
                wrapperClassName="!px-10"
                onClick={onClickRequest}
              />
            );
          }
        }}
      />

      <div className="bg-[var(--whiteColor)] rounded-[1rem] min-h-[10rem] mt-10 flex flex-col justify-center px-4 py-6 gap-y-5 overflow-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-x-5">
            <RadioButton
              title="All Request"
              value="ALL_REQUEST"
              checked={selectedRadio === "ALL_REQUEST"}
              onChange={(e) => onChangeRadio(e, null)}
            />
            <RadioButton
              title="Pending"
              value="PENDING"
              checked={selectedRadio === "PENDING"}
              onChange={(e) => onChangeRadio(e, "PENDING")}
            />
            <RadioButton
              title="Approved"
              value="APPROVED"
              checked={selectedRadio === "APPROVED"}
              onChange={(e) => onChangeRadio(e, "APPROVED")}
            />
          </div>
          <div className="flex items-center gap-x-3">
            <div className="font-size-medium">Date Range</div>
            <DatePicker
              startDate={startDate}
              endDate={endDate}
              onChange={onChangeDate}
              // maxDate={new Date()}
              dateRange
            />
          </div>
        </div>
        <CustomTable
          data={data}
          columns={columns}
          centerAlignedColumns={[
            "l1_status",
            "l2_status",
            "amount",
            "ac_status",
            "approved_amount",
          ]}
        />
      </div>
      {isRejectVisible.isOpen && (
        <RejectModal
          isModalVisible={isRejectVisible}
          setIsModalVisible={setIsRejectVisible}
          rejectHandler={({ data = [], comment = "" }) => {
            const validIds = [...new Set([...data])];
            rejectRequestHandler(validIds, comment);
          }}
          title="Imprest"
        />
      )}
      {isEditApproveVisible.isOpen && (
        <EditApproveModal
          isModalVisible={isEditApproveVisible}
          setIsModalVisible={setIsEditApproveVisible}
          approveHandler={({ data = [], comment = "", amount }) => {
            const validIds = [...new Set([...data])];
            approveRequestHandler(validIds, comment, amount);
          }}
          ids={isEditApproveVisible?.previousAmounts}
          totalAmount={isEditApproveVisible?.previousAmounts?.[0]?.totalAmount}
          title="Imprest"
        />
      )}
    </div>
  );
};

export default PettyCash;
